import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronRight, Wrench, ArrowRight, Shield, Zap, Building2 } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';
import { 
  PageHeader, 
  SectionHeader, 
  Section, 
  Container, 
  FadeInOnScroll,
  PrimaryButton 
} from '../components/DesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [designServices, setDesignServices] = useState([]);
  const [consultancyServices, setConsultancyServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      const services = response.data.services || [];
      setDesignServices(services.filter(s => s.category === 'design'));
      setConsultancyServices(services.filter(s => s.category === 'consultancy'));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Our Services"
        description="Comprehensive MEP engineering services including Mechanical Design, Electrical Design, Public Health, Lighting, Building Physics, and expert consultancy services across the UK."
        keywords="MEP services UK, mechanical engineering services, electrical engineering, public health design, HVAC consultancy, building services, decarbonisation analysis"
      />
      
      {/* Hero Section */}
      <PageHeader
        badge="What We Do"
        badgeIcon={Wrench}
        title="Our Services"
        subtitle="Comprehensive MEP engineering and design consultancy services tailored to meet the unique demands of your project."
      />

      {/* Why These Services Matter */}
      <Section background="light" padding="default">
        <Container>
          <FadeInOnScroll>
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-bold text-white">Why Professional MEP Services Matter in the UK</h2>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Regulatory Compliance</h3>
                        <p className="text-gray-600 leading-relaxed">
                          UK Building Regulations (Part L, F, P) require strict compliance for energy efficiency, ventilation, and electrical safety. 
                          Professional MEP consultancy ensures your project meets all statutory requirements.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Zap className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Energy Performance & Sustainability</h3>
                        <p className="text-gray-600 leading-relaxed">
                          With the UK's commitment to Net Zero by 2050, efficient MEP design directly impacts operating costs 
                          and EPC ratings, crucial for property value and marketability.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Risk Mitigation & Quality</h3>
                        <p className="text-gray-600 leading-relaxed">
                          Poor MEP design leads to uncomfortable environments and system failures. Professional services 
                          ensure coordinated, efficient systems that perform reliably.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ChevronRight className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Future-Proofing</h3>
                        <p className="text-gray-600 leading-relaxed">
                          Expert MEP consultancy considers future technologies and changing regulations, protecting your investment 
                          and ensuring compliance for years to come.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInOnScroll>
        </Container>
      </Section>

      {/* Services Tabs */}
      <Section background="white" padding="default">
        <Container>
          <Tabs defaultValue="design" className="w-full">
            <FadeInOnScroll>
              <TabsList className="grid w-full max-w-lg mx-auto grid-cols-2 mb-12 bg-gray-100 p-1 rounded-xl">
                <TabsTrigger 
                  value="design" 
                  className="text-base font-semibold rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  Design Services
                </TabsTrigger>
                <TabsTrigger 
                  value="consultancy" 
                  className="text-base font-semibold rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  Consultancy Services
                </TabsTrigger>
              </TabsList>
            </FadeInOnScroll>

            <TabsContent value="design">
              <FadeInOnScroll>
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Design Services</h2>
                  <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                    Our design services cover the full spectrum of MEP engineering, ensuring optimal 
                    performance, efficiency, and compliance for your building projects.
                  </p>
                </div>
              </FadeInOnScroll>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {designServices.map((service, index) => (
                  <FadeInOnScroll key={service._id} delay={index * 100}>
                    <Link to={`/services/${service.id}`} className="group block h-full">
                      <Card className="border-0 shadow-sm hover:shadow-xl h-full overflow-hidden bg-white hover:border-blue-200 border border-gray-100 rounded-2xl scroll-smooth-card">
                        <div className="h-48 scroll-smooth-image relative">
                          {service.image ? (
                            <img
                              src={service.image}
                              alt={service.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                              <Wrench className="w-12 h-12 text-blue-300" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                          <p className="text-gray-500 mb-4 line-clamp-3 leading-relaxed">{service.description}</p>
                          <span className="text-blue-600 font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all relative">
                            Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  </FadeInOnScroll>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="consultancy">
              <FadeInOnScroll>
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Consultancy Services</h2>
                  <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                    Expert advisory services to support your project from conception through to completion 
                    and beyond, ensuring compliance, efficiency, and value.
                  </p>
                </div>
              </FadeInOnScroll>
              <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                {consultancyServices.map((service, index) => (
                  <FadeInOnScroll key={service._id} delay={index * 100}>
                    <Link to={`/services/${service.id}`} className="group block h-full">
                      <Card className="border-0 shadow-sm hover:shadow-xl h-full bg-white hover:border-blue-200 border border-gray-100 rounded-2xl scroll-smooth-card">
                        <CardContent className="p-8">
                          <div className="flex items-start gap-5 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:from-blue-600 group-hover:to-blue-500 transition-colors duration-300 shadow-sm">
                              <Wrench className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                              <p className="text-gray-500 leading-relaxed line-clamp-3">{service.description}</p>
                            </div>
                          </div>
                          <span className="text-blue-600 font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all relative">
                            Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  </FadeInOnScroll>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Ready to Start Your Project?</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Get in touch with our team to discuss how we can support your MEP engineering needs.
            </p>
            <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
              Contact Us Today
            </PrimaryButton>
          </FadeInOnScroll>
        </Container>
      </Section>
    </div>
  );
};

export default Services;
